import * as React from 'react';
import {
  Badge,
  Button,
  ButtonDropdown,
  ColumnLayout,
  Header,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react-v3';
import { TagsList } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { DatabaseInfo, GetDataSetResponseStructure } from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';
import {
  dataClassificationOptions,
  defaultClassificationOption,
  DZ_GLUE_DATASOURCE_ID,
  DZ_REDSHIFT_DATASOURCE_ID,
  GALAXI_DATASOURCE_ID,
  PUBLIC_DATA_CLASSIFICATION,
  PUBLIC_DATA_CLASSIFICATION_ACCURATE_FROM,
} from 'src/commons/constants';
import { SelectProps } from '@amzn/awsui-components-react-v3/polaris/select/interfaces';

export const DetailsPageHeader = ({ header, buttons, versionSelect, editButton, description, cti }) => {
  return (
    <SpaceBetween direction='vertical' size='xs'>
      <Header
        variant={'h1'}
        actions={
          <SpaceBetween size={'s'} direction='horizontal'>
            {!!editButton && !editButton.hidden && (
              <Button
                href={editButton.href || ''}
                iconName={editButton.icon}
                onClick={editButton.onItemClick}
                key={0}
                loading={editButton.loading}
                variant={editButton.variant || 'normal'}
              >
                {editButton.text}
              </Button>
            )}
            {versionSelect}
            {buttons.map((button, key) =>
              !button.items ? (
                <Button
                  href={button.href || ''}
                  disabled={button.disabled || false}
                  iconName={button.icon}
                  onClick={button.onItemClick}
                  key={key}
                  loading={button.loading || false}
                  variant={button.variant || 'normal'}
                >
                  {button.text}
                </Button>
              ) : (
                <ButtonDropdown
                  items={button.items}
                  key={key}
                  onItemClick={button.onItemClick}
                  loading={button.loading}
                >
                  {button.text}
                </ButtonDropdown>
              ),
            )}
          </SpaceBetween>
        }
      >
        {header}
      </Header>

      <ColumnLayout columns={2}>
        <div> {description} </div>
        <div style={{ textAlign: 'right' }}>
          <b>Owner CTI: </b>
          {cti}
        </div>
      </ColumnLayout>
    </SpaceBetween>
  );
};

const getClassificationColor = (level: string) => {
  switch (level) {
    case 'Public':
      return 'green';
    case 'Confidential':
      return 'severity-low';
    case 'HighlyConfidential':
      return 'severity-medium';
    case 'Restricted':
      return 'severity-high';
    case 'Critical':
      return 'severity-critical';
    default:
      return 'severity-neutral';
  }
};

export const ClassificationBadge = ({
  classification,
  disabled,
  includeClassificationPrefix = false,
}: {
  classification: string;
  disabled?: boolean;
  includeClassificationPrefix?: boolean;
}) => (
  <Badge color={disabled ? 'grey' : getClassificationColor(classification)}>
    {includeClassificationPrefix ? 'AWS classification:' : ''} {classification}
  </Badge>
);

export const findOption = (
  options: SelectProps.Option[],
  value: string,
  defaultValue?: SelectProps.Option,
): SelectProps.Option => {
  return options.find((c) => c.value == value) ?? defaultValue;
};

export const getDataClassificationOption = (dataClassification: string): SelectProps.Option => {
  return findOption(dataClassificationOptions, dataClassification, defaultClassificationOption);
};

const isValidISODate = (isoString: string) => {
  if (!isoString) return false;
  const parsedDate = Date.parse(isoString);
  return !isNaN(parsedDate);
};

export const canDisplayDataClassification = (
  dataClassification: string | undefined,
  createDate: string | undefined,
): boolean => {
  if (!dataClassification) return false;
  const isValidClassification = dataClassificationOptions.map((option) => option.value).includes(dataClassification);
  const isCreateDateValid = isValidISODate(createDate);
  const isPublicClassificationAccurate =
    isCreateDateValid && new Date(createDate) > PUBLIC_DATA_CLASSIFICATION_ACCURATE_FROM;
  return isValidClassification && (dataClassification !== PUBLIC_DATA_CLASSIFICATION || isPublicClassificationAccurate);
};

interface iDataBadges {
  database?: DatabaseInfo;
  dataset?: GetDataSetResponseStructure;
  tags?: TagsList;
  businessGlossaries?: [];
  governanceTags?: [];
  hasLineage?: boolean;
  hasDataQuality?: boolean;
}

export const DataBadges = ({
  dataset,
  tags,
  businessGlossaries,
  database,
  governanceTags,
  hasLineage,
  hasDataQuality,
}: iDataBadges) => (
  <SpaceBetween size='xxs' direction='vertical'>
    <SpaceBetween size='xs' direction='horizontal'>
      {dataset?.PII ? <Badge color='red'>PII dataset</Badge> : ''}

      {dataset?.TableState && (
        <Badge color={dataset?.TableState === 'Active' ? 'green' : 'red'}>Status: {dataset?.TableState}</Badge>
      )}

      {dataset?.DataClassification && canDisplayDataClassification(dataset?.DataClassification, dataset?.CreatedOn) && (
        <ClassificationBadge classification={dataset?.DataClassification} includeClassificationPrefix={true} />
      )}

      {businessGlossaries?.map((businessGlossary) => {
        return <Badge color='green'>{businessGlossary['GlossaryValue']}</Badge>;
      })}

      {(dataset?.DataSourceType == GALAXI_DATASOURCE_ID || database?.DataSourceId == GALAXI_DATASOURCE_ID) && (
        <Badge color='blue'>Galaxi</Badge>
      )}

      {(dataset?.DataSourceType == DZ_REDSHIFT_DATASOURCE_ID ||
        database?.DataSourceId == DZ_REDSHIFT_DATASOURCE_ID ||
        dataset?.DataSourceType == DZ_GLUE_DATASOURCE_ID ||
        database?.DataSourceId == DZ_GLUE_DATASOURCE_ID) && <Badge color='grey'>DataZone</Badge>}

      {tags?.map((tag, index) => {
        return (
          <Badge key={index} color='blue'>
            {tag.tagKey}.{tag.tagValue}{' '}
          </Badge>
        );
      })}
    </SpaceBetween>
    <SpaceBetween size='xs' direction='horizontal'>
      {governanceTags === undefined && <Spinner size='normal' />}
      {governanceTags?.map((governanceTag) => (
        <Badge color='grey'>Governance tag: {governanceTag['GlossaryValue']}</Badge>
      ))}
    </SpaceBetween>
    <SpaceBetween size='xs' direction='horizontal'>
      {!!hasLineage && hasLineage && <Badge color={'green'}>Data lineage</Badge>}
      {!!hasDataQuality && hasDataQuality && <Badge color={'green'}>Data quality</Badge>}
    </SpaceBetween>
  </SpaceBetween>
);
